import type { CommonColors } from '@mui/material/styles/createPalette';

import type { PaletteColorNoChannels } from './core/palette';
import type { ThemeDirection, ThemeColorScheme, ThemeCssVariables } from './types';

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    PaletteColorNoChannels
  > & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<
      '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  direction: 'ltr',
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  classesPrefix: 'minimal',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'new-hero',
    secondary: 'new-hero',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#fff9f8',
      light: '#fff9f8',
      main: '#fc6743',
      dark: '#ce5335',
      darker: '#ce5335',
      contrastText: '#FFF9F8',
    },
    secondary: {
      lighter: '#FFFCFA',
      light: '#FFFCFA',
      main: '#FC9256',
      dark: '#B45824',
      darker: '#B45824',
      contrastText: '#000000',
    },
    info: {
      lighter: '#E4F4FD',
      light: '#E4F4FD',
      main: '#98BFDE',
      dark: '#295A81',
      darker: '#295A81',
      contrastText: '#FAFDFF',
    },
    success: {
      lighter: '#D3FCD2',
      light: '#E6FCF2',
      main: '#17B26A',
      dark: '#067647',
      darker: '#065E49',
      contrastText: '#E6FCF2',
    },
    warning: {
      lighter: '#FFFFFF',
      light: '#FFF5EC',
      main: '#ED6C02',
      dark: '#E65100',
      darker: '#E65100',
      contrastText: '#FFFFFF',
    },
    error: {
      lighter: '#FFFFFF',
      light: '#FFF4F3',
      main: '#D32F2F',
      dark: '#C62828',
      darker: '#C62828',
      contrastText: '#FFFFFF',
    },
    grey: {
      '50': '#FAFAFA',
      '100': '#F5F5F5',
      '200': '#EEEEEE',
      '300': '#E0E0E0',
      '400': '#C4CDD5',
      '500': '#9E9E9E',
      '600': '#757575',
      '700': '#616161',
      '800': '#424242',
      '900': '#212121',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
};
